import { useEffect, Suspense } from "react";
import MenuItem from "@Components/Header/MenuItem";
import { COMPONENTS_CATEGORY, EVENT_LABEL, FACEBOOK_MESSENGER_URL } from "constants/index";
import {
  EMAIL_1,
  HELP_URL,
  HOW_IT_WORKS_URL,
  PHONE_7,
  PHONE_8,
  PHONE_9,
  WHATSAPP_URL,
} from "constants/configConstants";
import { HK_WEIXIN_MWEB } from "helpers/getWeComUrlBasedOnSource";
import HowItWorks from "@Icons/HowItWorks";
import HowItWorksIcon from "@Icons/HowItWorksIcon";
import WechatGreenOutline from "@Icons/WechatGreenOutline";
import PhoneIcon from "@Icons/PhoneIcon";
import EnvelopeIcon from "@Icons/EnvelopeIcon";
import handleChatWidgetOpen from "helpers/handleChatWidgetOpen";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import config from "@Config/index";
import LoginIcon from "@Icons/LoginIcon";
import HeadPhoneIcon from "@Icons/HeadPhoneIcon";
import MessengerColor from "@Icons/MessengerColor";
import WhatsAppColor from "@Icons/WhatsAppColor";
import ChatColor from "@Icons/ChatColor";
import DropDownMenu from "@Components/Header/DropDownMenu";
import FullPageLoader from "@Components/FullPageLoader";
import wrapperHOC from "@Utils/wrapperHOC";
import styles from "./fullScreenMobileMenu.module.scss";
import useTranslationHook from "hooks/useTranslationHook";

interface FullScreenMobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginClick: () => void;
  isUserLoggedIn?: boolean;
}

const FullScreenMobileMenu = ({
  isOpen,
  onClose,
  onLoginClick,
  isUserLoggedIn = false,
}: FullScreenMobileMenuProps) => {
  const {
    HELP_CENTRE_CLICKED,
    HOW_IT_WORKS_CLICKED,
    WHATSAPP_CLICKED,
    FACEBOOK_CLICKED,
    PHONE_CLICKED,
    EMAIL_CLICKED,
    WECHAT_CLICKED,
    QUICK_CHAT_CLICKED,
  } = EVENT_LABEL;
  const { t } = useTranslationHook();

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const handleZendeskClick = () => {
    handleEventEmitter({ action: QUICK_CHAT_CLICKED });
    handleChatWidgetOpen(true);
    onClose();
  };

  const handleEventEmitter = ({ action, label = "", ...rest }: any) => {
    gtagEventEmitter({
      action,
      category: COMPONENTS_CATEGORY.TOP_NAV,
      label: `list_page ${label}`,
      ...rest,
    });
  };

  const handleLoginClick = () => {
    onLoginClick();
    onClose();
  };

  if (!isOpen) return null;

  if (isUserLoggedIn) {
    return (
      <div className={styles.overlay}>
        <div className={styles.menu}>
          <div className={styles.header}>
            <h2 className={styles.title}>{t("list.menu.title")}</h2>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>

          <div className={styles.globalMenuContent}>
            <Suspense fallback={<FullPageLoader />}>
              <DropDownMenu
                showAppBanner
                handleEventEmitter={handleEventEmitter}
                isReferralPage={false}
                isAmberPlusHeader={false}
                isBlogHeader={false}
                hideExtraMenuItems={false}
              />
            </Suspense>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.menu}>
        <div className={styles.header}>
          <h2 className={styles.title}>{t("list.menu.title")}</h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              <HeadPhoneIcon fill="#6B7280" width={20} height={20} />
              {t("list.menu.support")}
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionSubtitle}>{t("list.menu.quickLinks")}</div>
            <div className={styles.quickLinks}>
              <MenuItem
                label={t("list.menu.helpCenter")}
                icon={<HowItWorks width={16} height={16} fill="#6B7280" />}
                redirectTo={HELP_URL}
                clickHandler={() => {
                  handleEventEmitter({ action: HELP_CENTRE_CLICKED, click_location: "support" });
                  onClose();
                }}
              />
              <MenuItem
                label={t("list.menu.howItWorks")}
                icon={<HowItWorksIcon />}
                redirectTo={HOW_IT_WORKS_URL}
                clickHandler={() => {
                  handleEventEmitter({ action: HOW_IT_WORKS_CLICKED });
                  onClose();
                }}
              />
            </div>
          </div>

          <div className={styles.divider} />

          <div className={styles.section}>
            <div className={styles.sectionSubtitle}>{t("list.menu.supportNow")}</div>
            <MenuItem
              label={t("list.menu.quickChat")}
              icon={<ChatColor width={18} height={18} />}
              rightSection={`<span style="color: white; font-size: 12px; font-weight: 500; padding: 4px 6px; border-radius: 16px; display: inline-block;">${t(
                "Online",
              )}</span>`}
              clickHandler={handleZendeskClick}
            />
          </div>

          <div className={styles.section}>
            {config.IS_CHINA_APP ? (
              <MenuItem
                label={t("list.menu.wechat")}
                icon={<WechatGreenOutline />}
                redirectTo={HK_WEIXIN_MWEB}
                clickHandler={() => {
                  handleEventEmitter({ action: WECHAT_CLICKED });
                  onClose();
                }}
              />
            ) : (
              <>
                <MenuItem
                  label={t("list.menu.whatsapp")}
                  icon={<WhatsAppColor width={16} height={16} />}
                  redirectTo={WHATSAPP_URL}
                  clickHandler={() => {
                    handleEventEmitter({ action: WHATSAPP_CLICKED });
                    onClose();
                  }}
                />

                <MenuItem
                  label={t("list.menu.facebookMessenger")}
                  icon={
                    <div className={styles.messengerIcon}>
                      <MessengerColor />
                    </div>
                  }
                  redirectTo={FACEBOOK_MESSENGER_URL}
                  clickHandler={() => {
                    handleEventEmitter({ action: FACEBOOK_CLICKED });
                    onClose();
                  }}
                />
              </>
            )}
          </div>

          <div className={styles.divider} />

          <div className={styles.section}>
            <MenuItem
              label={PHONE_7.full_number}
              redirectTo={`tel:${PHONE_7.full_number}`}
              icon={<PhoneIcon width={16} height={16} fill="#6B7280" />}
              clickHandler={() => {
                handleEventEmitter(PHONE_CLICKED);
                onClose();
              }}
            />
            <MenuItem
              label={PHONE_8.full_number}
              redirectTo={`tel:${PHONE_8.full_number}`}
              icon={<PhoneIcon width={16} height={16} fill="#6B7280" />}
              clickHandler={() => {
                handleEventEmitter(PHONE_CLICKED);
                onClose();
              }}
            />
            <MenuItem
              label={PHONE_9.full_number}
              redirectTo={`tel:${PHONE_9.full_number}`}
              icon={<PhoneIcon width={16} height={16} fill="#6B7280" />}
              clickHandler={() => {
                handleEventEmitter(PHONE_CLICKED);
                onClose();
              }}
            />

            <MenuItem
              label={EMAIL_1}
              redirectTo={`mailto:${EMAIL_1}`}
              icon={<EnvelopeIcon width={16} height={16} fill="#6B7280" />}
              clickHandler={() => {
                handleEventEmitter({ action: EMAIL_CLICKED });
                onClose();
              }}
            />
          </div>

          <div className={styles.loginSection}>
            <button type="button" className={styles.loginButton} onClick={handleLoginClick}>
              <LoginIcon fill="#6B7280" />
              {t("list.menu.login")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default wrapperHOC(FullScreenMobileMenu, {
  componentName: "FullScreenMobileMenu",
  showForChina: true,
});
